import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, SubscriptionFromQuery, UserData } from './AuthContext';
import {
  MultiFactorResolver,
  PhoneMultiFactorInfo,
  signOut,
  User,
} from 'firebase/auth';
import {
  Roles,
  SignInProvider,
  UserHasuraClaims,
  UsersWithRole,
} from '../../firebase/interfaces';
import {
  ADMIN_ROLES,
  DEFAULT_PRIVATE_LD_ATTRIBUTES,
  LOCAL_STORAGE_USER_FROM_QR,
  LOCAL_STORAGE_USER_SCANNED_TEST_KIT_ID,
  LOCAL_STORAGE_USER_SCANNED_TRACKING_NUMBER,
} from 'utilities/constants';
import { getFirebaseAuth } from '../../firebase/config';
import {
  useFetchUserByEmail,
  useFetchUserFromHasuraOrFhir,
  useMFA,
  userHasCustomClaimsAndRole,
} from '../../firebase/hooks';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { clearLocalStorageExceptKeys } from 'utilities/functions';
import { CodexFeatureFlagsKinds } from 'utilities/interfaces';
import { LOCAL_STORAGE_WEATHER_ZIP } from 'hooks/useWeather/constants';
import StorageService from 'utilities/storageService';
import {
  ALLOWED_ROLES_CUSTOM_CLAIM,
  HASURA_USER_STORAGE_KEY,
  JWT_CUSTOM_CLAIMS,
} from '../../firebase/constants';
import { FullPageLoader } from 'components/loaderComponent';
import {
  useDeleteUserSessionMutation,
  useGetSignUrlFromStorageForRequesterLazyQuery,
  useGetStripeSubscriptionsQuery,
} from 'graphql/generated/hasura';
import { useAccounts } from 'hooks/useAccounts';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';

interface AuthProviderProps {
  children: React.ReactNode;
}

const hasuraUserStorage = new StorageService(HASURA_USER_STORAGE_KEY);

let isFirstRendered = false;
const grStore = new StorageService(LOCAL_STORAGE_USER_FROM_QR);
export const ACTIVE_STATUS = 1;
export const TRIAL_STATUS = 2;
export const SUBSCRIPTION_ACTIVE_STATUS = [ACTIVE_STATUS, TRIAL_STATUS];
const SUBSCRIPTION_FINISH_STATUS = [3, 4, 8];
export const FINISH_PERIOD_TIME = 14;

export type AccountInfo = {
  id: string | null;
  blockedAt?: string | null;
  closedAt?: string | null;
  name: string;
  avatarImage?: string | null;
  isChild?: boolean;
  isVerify: boolean;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [profilePicture, setProfilePictureResolver] = useState<string | null>(
    null,
  );
  const [mfaResolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [isAuthResolved, setAuthResolved] = useState(false);
  const [loadingUserFullInfo, setLoadingUserFullInfo] = useState(true);

  const [deleteUserSession] = useDeleteUserSessionMutation({
    onCompleted: async () => {
      clearAccountInfo();
      setAuthResolved(true);
      clearLocalStorageExceptKeys([
        LOCAL_STORAGE_WEATHER_ZIP,
        LOCAL_STORAGE_USER_SCANNED_TEST_KIT_ID,
        LOCAL_STORAGE_USER_SCANNED_TRACKING_NUMBER,
        HASURA_USER_STORAGE_KEY,
      ]);
      await client.resetStore().then(() => signOut(firebaseAuth));
      setUser(null);
      setLoggedIn(false);
    },
    onError: async () => {
      clearAccountInfo();
      setAuthResolved(true);
      clearLocalStorageExceptKeys([
        LOCAL_STORAGE_WEATHER_ZIP,
        LOCAL_STORAGE_USER_SCANNED_TEST_KIT_ID,
        LOCAL_STORAGE_USER_SCANNED_TRACKING_NUMBER,
        HASURA_USER_STORAGE_KEY,
      ]);
      await client.resetStore().then(() => signOut(firebaseAuth));
      setUser(null);
      setLoggedIn(false);
    },
  });
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const {
    fetchUserDataFromDifferentSource,
    fetchProvidersByPatientId,
    getDelegatedAdmin,
  } = useFetchUserFromHasuraOrFhir();
  const { data: stripeSubscription, refetch: refetchSubscriptions } =
    useGetStripeSubscriptionsQuery({
      skip: user?.role !== Roles.PROVIDER,
    });

  const [getFileUrlFromStorageForRequester] =
    useGetSignUrlFromStorageForRequesterLazyQuery();

  const client = useApolloClient();

  const {
    accounts,
    selectedAccount,
    selectAccount,
    loadingSubPatient,
    mainAccountInfo,
    refetchSubPatients,
    clearInfo: clearAccountInfo,
    avatarMap,
  } = useAccounts(user, loadingUserFullInfo);

  const getAvatar = useCallback(
    async (id: string) => {
      let profileImage = null;
      if (!profilePicture || !id) return profileImage;
      if (avatarMap?.has(id)) {
        return avatarMap?.get(id);
      }
      profileImage = await getFileUrlFromStorageForRequester({
        variables: {
          fileId: profilePicture,
          requestedUserId: id,
        },
      });
      return profileImage?.data?.GetSignUrlFromStorageForRequester?.url;
    },
    [avatarMap, getFileUrlFromStorageForRequester, profilePicture],
  );

  const getSelectedAccount = useCallback(
    () => selectedAccount,
    [selectedAccount],
  );

  const handleSelectAccount = (id: string | null) => {
    selectAccount(id);
    isFirstRendered = false;
  };

  const handleReloadUserData = useCallback(() => {
    isFirstRendered = false;
  }, []);

  const { getEnrolledFactors } = useMFA();
  const navigate = useNavigate();
  const ldClient = useLDClient();

  const handleReloadDataAfterDeleteAccount = useCallback(
    (id: string) => {
      refetchSubPatients();
      selectAccount(null);
      handleReloadUserData();
      setTimeout(() => navigate(`/download-child-data/${id}`), 1500);
    },
    [handleReloadUserData, navigate, refetchSubPatients, selectAccount],
  );

  const firebaseAuth = getFirebaseAuth();

  const login = useCallback(
    (
      redirect?: string,
      shouldLoginBeforeRedirect?: boolean,
      shouldSkipRedirectBlock?: boolean,
    ) => {
      const userFaerbase = firebaseAuth.currentUser;
      if (userFaerbase && userFaerbase.emailVerified) {
        const enrolledFactors = getEnrolledFactors();
        if (!enrolledFactors || enrolledFactors.length === 0) {
          shouldLoginBeforeRedirect && setLoggedIn(true);
          shouldSkipRedirectBlock &&
            redirect &&
            navigate(redirect, {
              state: { email: userFaerbase.email, skipBlocker: true },
            });
          !shouldSkipRedirectBlock &&
            redirect &&
            navigate(redirect, { state: { email: userFaerbase.email } });
        } else {
          const enrolledFactor = enrolledFactors.at(-1) as PhoneMultiFactorInfo;
          setMFAPhoneNumber(enrolledFactor.phoneNumber);
          setLoggedIn(true);

          shouldLoginBeforeRedirect &&
            shouldSkipRedirectBlock &&
            redirect &&
            navigate(redirect, {
              state: { email: userFaerbase.email, skipBlocker: true },
            });
        }
      }
      if (user?.role === Roles.PATIENT) {
        refetchSubPatients();
      }
      setAuthResolved(true);
    },
    [
      firebaseAuth.currentUser,
      getEnrolledFactors,
      navigate,
      refetchSubPatients,
      user,
    ],
  );

  const loginWithOutMFA = useCallback(
    (redirect?: string) => {
      const userFaerbase = firebaseAuth.currentUser;
      if (userFaerbase && userFaerbase.emailVerified) {
        setLoggedIn(true);
        redirect && navigate(redirect);
      }
      if (user?.role === Roles.PATIENT) {
        refetchSubPatients();
      }
      setAuthResolved(true);
    },
    [firebaseAuth, navigate, refetchSubPatients, user],
  );

  const logout = useCallback(async () => {
    try {
      if (isLoggedIn) {
        await deleteUserSession();
      }
    } catch (error) {
      console.error(error);
      setProfilePicture(null);
      setMfaResolver(null);
      setMFAPhoneNumber('');
      setPhoneNumber('');
      clearLocalStorageExceptKeys([
        LOCAL_STORAGE_WEATHER_ZIP,
        LOCAL_STORAGE_USER_SCANNED_TEST_KIT_ID,
        LOCAL_STORAGE_USER_SCANNED_TRACKING_NUMBER,
        HASURA_USER_STORAGE_KEY,
      ]);
    } finally {
      clearAccountInfo();
      setAuthResolved(true);
      await client.resetStore().then(() => signOut(firebaseAuth));
      setUser(null);
      setLoggedIn(false);
    }
  }, [isLoggedIn, deleteUserSession, clearAccountInfo, client, firebaseAuth]);

  useEffect(() => {
    if (!isFirstRendered) {
      isFirstRendered = true;
      firebaseAuth.onAuthStateChanged(async (currentUser: User | null) => {
        try {
          setLoadingUserFullInfo(true);
          if (currentUser && currentUser.email) {
            const { signInProvider, claims } =
              await currentUser.getIdTokenResult();
            const customClaims = claims[JWT_CUSTOM_CLAIMS] as UserHasuraClaims;
            const allowedRoles =
              customClaims?.[ALLOWED_ROLES_CUSTOM_CLAIM] || [];

            const { hasCustomClaims, role } =
              await userHasCustomClaimsAndRole();

            if (hasCustomClaims) {
              const user = await fetchUserByEmailPromise(currentUser.email);

              if (role === Roles.DELEGATED_ADMIN) {
                const { data } = await getDelegatedAdmin({
                  variables: { delegatedAdminCodexId: user.id },
                });
                const userDataFromSource = data?.getDelegatedAdminDetails;
                const userData = {
                  email: currentUser.email,
                  uuid: user.id,
                  account_closed_at: user?.account_closed_at,
                  blocked_at: user?.blocked_at,
                  displayName:
                    `${userDataFromSource?.firstName} ${userDataFromSource?.lastName}`.trim(),
                  deletedByEmail: userDataFromSource?.deletedByEmail,
                  signInProvider: signInProvider as SignInProvider,
                  permissions: userDataFromSource?.permissions,
                  role:
                    allowedRoles.length > 0
                      ? (allowedRoles[allowedRoles.length - 1] as Roles)
                      : Roles.PATIENT,
                };

                setUser((user) => ({ ...user, ...userData }));

                hasuraUserStorage.setData({
                  ...user,
                  ...userDataFromSource,
                });

                await ldClient?.identify({
                  kind: CodexFeatureFlagsKinds.patient,
                  key: user.id,
                  email: currentUser.email,
                  privateAttributeNames: DEFAULT_PRIVATE_LD_ATTRIBUTES,
                });
                loginWithOutMFA();

                setLoadingUserFullInfo(false);
                return;
              }

              const userDataFromSource = await fetchUserDataFromDifferentSource(
                currentUser.email || '',
                role as Roles,
              );

              const providerId = await fetchProvidersByPatientId(
                user.id,
                role as Roles,
              );

              const userData = {
                email: currentUser.email,
                uuid: user.id,
                account_closed_at: user?.account_closed_at,
                blocked_at: user?.blocked_at,
                displayName:
                  `${userDataFromSource?.SENSITIVE_firstname} ${userDataFromSource?.SENSITIVE_lastname}`.trim(),
                providerEnabled: userDataFromSource?.enabled,
                signInProvider: signInProvider as SignInProvider,
                role:
                  allowedRoles.length > 0
                    ? (allowedRoles[allowedRoles.length - 1] as Roles)
                    : Roles.PATIENT,
              };

              setUser((user) => ({ ...user, ...userData }));

              hasuraUserStorage.setData({
                ...user,
                ...userDataFromSource,
                providerId,
              });

              await ldClient?.identify({
                kind: CodexFeatureFlagsKinds.patient,
                key: user.id,
                email: currentUser.email,
                privateAttributeNames: DEFAULT_PRIVATE_LD_ATTRIBUTES,
              });

              const profile = (await getAvatar(user.id)) || null;
              setProfilePicture(profile);

              setUser((userData) => {
                return {
                  ...userData,
                  profilePicture: profile,
                } as UserData;
              });

              if (
                userDataFromSource?.role &&
                userDataFromSource.role === Roles.PROVIDER
              ) {
                await refetchSubscriptions();
                setUser((userData) => {
                  return {
                    ...userData,
                    providerEnabled: userDataFromSource?.enabled,
                    role: userDataFromSource?.role,
                  } as UserData;
                });
                if (userDataFromSource?.enabled !== undefined) {
                  setUser((userData) => {
                    return {
                      ...userData,
                      providerEnabled: userDataFromSource?.enabled,
                    } as UserData;
                  });
                }
                if (userDataFromSource.SENSITIVE_firstname) {
                  setUser((userData) => {
                    return {
                      ...userData,
                      displayName:
                        `${userDataFromSource?.SENSITIVE_firstname} ${userDataFromSource?.SENSITIVE_lastname}`.trim(),
                    } as UserData;
                  });
                }
                if (userDataFromSource.SENSITIVE_phone) {
                  setPhoneNumber(userDataFromSource.SENSITIVE_phone);
                }
              } else if (
                userDataFromSource?.role &&
                userDataFromSource.role === Roles.PATIENT
              ) {
                setUser((userData) => {
                  return {
                    ...userData,
                    uuid: user.id,
                    providerId,
                    parentId: (userDataFromSource as UsersWithRole)?.parentId,
                    state:
                      userDataFromSource?.SENSITIVE_address_information
                        ?.state || userData?.state,
                    role: userDataFromSource?.role,
                  } as UserData;
                });

                if (userDataFromSource.SENSITIVE_firstname) {
                  setUser((userData) => {
                    return {
                      ...userData,
                      displayName:
                        `${userDataFromSource?.SENSITIVE_firstname} ${userDataFromSource?.SENSITIVE_lastname}`.trim(),
                    } as UserData;
                  });
                }

                if (userDataFromSource.SENSITIVE_phone) {
                  setPhoneNumber(userDataFromSource.SENSITIVE_phone);
                }

                if (
                  'SENSITIVE_country' in userDataFromSource &&
                  userDataFromSource.SENSITIVE_country
                ) {
                  setCountry(userDataFromSource.SENSITIVE_country);
                }
              }

              if (userDataFromSource?.SENSITIVE_profile_picture_id) {
                setUser((userData) => {
                  return {
                    ...userData,
                    profilePicture:
                      userDataFromSource?.SENSITIVE_profile_picture_id || null,
                  } as UserData;
                });
              }

              grStore.setData('false');

              loginWithOutMFA();

              setLoadingUserFullInfo(false);

              // if (process.env.REACT_APP_IS_DEBUG_MODE) {
              //   loginWithOutMFA();
              // } else {
              //   login();
              // }
            }
          } else {
            grStore.clear();
            await logout();
          }
        } catch (error) {
          console.error(error);
        } finally {
          setAuthResolved(true);
        }
      });
    }
  }, [
    ldClient,
    fetchUserByEmailPromise,
    login,
    logout,
    fetchUserDataFromDifferentSource,
    loginWithOutMFA,
    fetchProvidersByPatientId,
    selectAccount,
    refetchSubscriptions,
    firebaseAuth,
    getDelegatedAdmin,
    getAvatar,
  ]);

  const setUserUuid = (uuid: string) => {
    setUser((user) => {
      return {
        ...user,
        uuid,
      } as UserData;
    });
  };

  const setMFAPhoneNumber = (mfaPhoneNumber: string) => {
    setUser((user) => {
      return {
        ...user,
        mfaPhoneNumber,
      } as UserData;
    });
  };

  const setPhoneNumber = (phoneNumber: string) => {
    setUser((user) => {
      return {
        ...user,
        phoneNumber,
      } as UserData;
    });
  };

  const setCountry = (country: string) => {
    setUser((user) => {
      return {
        ...user,
        country: country || user?.country,
      } as UserData;
    });
  };

  const setDisplayName = (displayName: string) => {
    setUser((user) => {
      return {
        ...user,
        displayName,
      } as UserData;
    });
  };

  const setAvatarUrl = (url: string) => {
    setUser((user) => {
      return {
        ...user,
        profilePicture: url,
      } as UserData;
    });
  };

  const setMfaResolver = (mfaResolver: MultiFactorResolver | null) =>
    setResolver(mfaResolver);

  const setProfilePicture = (profilePicture: string | null) =>
    setProfilePictureResolver(profilePicture);

  const setRole = (role: Roles) => {
    if (!user) return;
    if (user.role === role) return;
    setUser({
      ...user,
      role,
    });
  };

  const setProviderEnabled = (providerEnabled: boolean) => {
    if (!user) return;
    if (user.providerEnabled === providerEnabled) return;
    setUser({
      ...user,
      providerEnabled,
    });
  };

  const hasActivePeriod = useMemo(
    () =>
      moment().isSameOrBefore(
        moment(
          stripeSubscription?.stripe_subscriptions?.[0]?.current_period_end,
        )
          .startOf('day')
          .subtract(FINISH_PERIOD_TIME * -1, 'days'),
      ),
    [stripeSubscription],
  );

  const hasActiveSubscription = useMemo(
    () =>
      SUBSCRIPTION_ACTIVE_STATUS.includes(
        stripeSubscription?.stripe_subscriptions?.[0]?.status as number,
      ) ||
      (SUBSCRIPTION_FINISH_STATUS.includes(
        stripeSubscription?.stripe_subscriptions?.[0]?.status as number,
      ) &&
        hasActivePeriod),
    [stripeSubscription, hasActivePeriod],
  );

  const authContextValue = {
    isLoggedIn,
    user,
    mfaResolver,
    isAuthResolved,
    profilePicture,
    setAvatarUrl,
    accounts,
    setRole,
    setUserUuid,
    setPhoneNumber,
    setMFAPhoneNumber,
    setCountry,
    setDisplayName,
    setMfaResolver,
    setProfilePicture,
    setProviderEnabled,
    refetchSubPatients,
    handleSelectAccount,
    login,
    loginWithOutMFA,
    logout,
    hasActiveSubscription,
    loadingUserFullInfo,
    selectedAccount,
    getSelectedAccount,
    avatarMap,
    mainAccountInfo,
    hasActivePeriod,
    handleReloadUserData,
    handleReloadDataAfterDeleteAccount,
    refetchSubscriptions,
    stripeSubscription:
      (stripeSubscription?.stripe_subscriptions as SubscriptionFromQuery[]) ??
      null,
  };

  const isAdmin = !!user?.role && ADMIN_ROLES.includes(user?.role);

  return (
    <>
      {isAuthResolved && !loadingSubPatient ? (
        <AuthContext.Provider value={authContextValue} key={selectedAccount}>
          <div className={isAdmin ? 'admin-pages' : ''}>{children}</div>
        </AuthContext.Provider>
      ) : (
        <FullPageLoader />
      )}
    </>
  );
};
